import { AfterViewInit, Component, EventEmitter, Input, OnChanges, Output, SimpleChanges, ViewChild } from '@angular/core';
import { DxDataGridComponent } from 'devextreme-angular';
import { Router } from '@angular/router';
import { ModalComponent, WorkspaceProperty, WorkspacePropertyIntegrationDetail } from 'app/internal/shared';
import { trigger } from 'devextreme/events';
import { Workbook } from 'exceljs';
import { saveAs } from 'file-saver';
import { exportDataGrid } from 'devextreme/excel_exporter';
import { AdminReassignPropertyModalComponent } from '../../admin-reassign-property-modal/admin-reassign-property-modal.component';
import { AnalyticsFileFormat, AnalyticsReportType, AnalyticsService } from 'app/shared/analytics';
import { IntegrationStatusType } from 'app/internal/shared/models/connected-apps';

@Component({
  selector: 'sp-admin-edit-portfolio-properties',
  templateUrl: './admin-edit-portfolio-properties.component.html',
  styleUrls: ['./admin-edit-portfolio-properties.component.scss'],
  standalone: false
})

export class AdminPortfolioPropertiesComponent implements AfterViewInit, OnChanges {

  @ViewChild('dxAdminPortfolioPropertiesDataGrid', { static: false }) dxAdminPortfolioPropertiesDataGrid: DxDataGridComponent;

  readonly allowedPageSizes = [10, 25, 50, 'all'];
  @Input() workspaceProperties: WorkspaceProperty[] = [];
  @Input() currentWorkspaceId: string;
  @Input() workspaceName: string;
  @Input() companyName: string;

  @Output() deactivate = new EventEmitter<string>();
  @Output() reactivate = new EventEmitter<string>();
  @Output() reassign = new EventEmitter<{ propertyId: string, destinationWorkspaceId: string, includeUsers: boolean }>();
  @Output() duplicate = new EventEmitter<string>();
  @Output() clearCache = new EventEmitter<string>();

  @ViewChild('deactivatePropertyStatusModal', { static: true }) public deactivatePropertyStatusModal: ModalComponent;
  @ViewChild('reactivatePropertyStatusModal', { static: true }) public reactivatePropertyStatusModal: ModalComponent;
  @ViewChild('reassignPropertyModal', { static: true }) public reassignPropertyModal: AdminReassignPropertyModalComponent;
  @ViewChild('duplicatePropertyModal', { static: true }) public duplicatePropertyModal: ModalComponent;
  @ViewChild('addConnectedAppModal', { static: true }) public addConnectedAppModal: ModalComponent;

  toggleStatusPropertyId: string;
  duplicatePropertyId: string;
  connectedAppPropertyId: string;
  connectedAppPropertyName: string;
  connectedAppIntegrationStatus: WorkspacePropertyIntegrationDetail;
  dataGridSource: any;
  activeFilterItems = [
    { value: 'active', text: 'Active' },
    { value: 'inactive', text: 'Inactive' },
    { value: 'all', text: 'All' }
  ];

  constructor(private router: Router,
    private analyticsService: AnalyticsService
  ) { }

  ngAfterViewInit(): void {
    this.dxAdminPortfolioPropertiesDataGrid?.instance?.beginCustomLoading('Loading...');
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.workspaceProperties) {
      this.updateDataGridSource('active');
    }
  }

  activeDropdownChanged(e: any) {
    this.updateDataGridSource(e.value);
  }

  updateDataGridSource(selectedValue: string) {
    if (selectedValue === 'active') {
      this.dataGridSource = this.workspaceProperties?.filter(x => x.isActive == true);
    } else if (selectedValue === 'inactive') {
      this.dataGridSource = this.workspaceProperties?.filter(x => x.isActive == false);
    } else {
      this.dataGridSource = this.workspaceProperties;
    }
  }

  dxCalculateCellValueForIsTestColumn(rowData: any) {
    return rowData.isTest ? 'Yes' : 'No';
  }

  dxCalculateCellValueForIntegrationIsActiveColumn(rowData: any) {
    if (rowData.integrationDetail == null ) {
      return ' ';
    } 
    
    return rowData.integrationDetail.isActive == true ? 'Yes' : 'No';
  }

  dxCalculateCellValueForLocationColumn(rowData: any) {
    return `${rowData.city}, ${rowData.state}`;
  }

  dxCalculateCellValueForIntegrationStatusColumn(rowData: any) {
    if (!rowData.integrationDetail || rowData.integrationDetail.lastUpdateStatus == null) {
      return ' ';
    }
    
    switch (rowData.integrationDetail.lastUpdateStatus) {
      case IntegrationStatusType.InProgress:
        return 'In Progress';
      case IntegrationStatusType.Successful:
        return 'Successful';
      case IntegrationStatusType.Failed:
        return 'Failed';
      default:
        return 'Unknown';
    }
  }

  dxGridRowPrepared(e) {
    // https://supportcenter.devexpress.com/ticket/details/t744353/datagrid-how-to-change-the-row-height    
    if (e.rowType === 'data') {
      e.rowElement.style.height = '60px';
    }
  }

  // For some reason, the 'onContentReady' is fired 2x.  We only care about the second call, so need
  // to add the counter to keep track of it.
  dxGridOnContentReadyCallCount = 0;
  dxGridOnContentReady() {
    this.dxGridOnContentReadyCallCount += 1;

    if (this.dxGridOnContentReadyCallCount === 2) {
      this.dxAdminPortfolioPropertiesDataGrid.instance.endCustomLoading();
    }
  }

  rebuildDxGrid() {
    this.dxAdminPortfolioPropertiesDataGrid.instance.refresh();
  }

  showActionMenu(e) {
    // This allows us to either left or right click to show the menu.
    const newEvent = Object.assign({}, e.event, { type: 'dxcontextmenu' });
    e.event.stopPropagation();
    trigger(e.element, newEvent);
  }

  onContextMenuPreparing(e) {

    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const that = this;

    // Only show the Context Menu if the Menu Button is clicked!
    if (e.row.rowType === 'data' && e.column.cellTemplate === 'propertyId') {

      const propertyId = e.row.data.propertyId;
      const propertyName = e.row.data.name;
      const integrationStatus = e.row.data.integrationDetail;

      const toggleActiveStatusMenuItem = {
        text: e.row.data.isActive ? 'Deactivate' : 'Reactivate',
        onItemClick: function () {
          that.toggleStatusPropertyId = propertyId;
          if (e.row.data.isActive) {
            that.deactivatePropertyStatusModal.show();
          } else {
            that.reactivatePropertyStatusModal.show();
          }
        }
      };

      e.items = [
        {
          text: 'Edit',
          onItemClick: function () {
            that.router.navigate(['/useraccount/admin/portfolios/' + that.currentWorkspaceId + '/edit-property/' + propertyId]);
          }
        },
        toggleActiveStatusMenuItem,
        {
          text: 'Reassign',
          onItemClick: function () {
            if (that.currentWorkspaceId) {
              that.reassignPropertyModal.show(propertyId, e.row.data.name, that.currentWorkspaceId);
            } else {
              alert('This Property isnt linked to a Workspace?!?');
            }
          }
        },
        {
          text: 'Duplicate',
          onItemClick: function () {
            that.duplicatePropertyId = propertyId;
            that.duplicatePropertyModal.show();
          }
        },
        {
          text: 'Clear Cache',
          onItemClick: function () {
            that.clearCache.emit(propertyId);
          }
        },
        {
          text: 'Connected Apps Settings',
          onItemClick: function () {
            that.connectedAppPropertyId = propertyId;
            that.connectedAppPropertyName = propertyName;
            that.connectedAppIntegrationStatus = integrationStatus;
            that.addConnectedAppModal.show();
          }
        },
      ];
    }
  }

  exportToExcel() {
    const workbook = new Workbook();
    const worksheet = workbook.addWorksheet('Properties');
    const currentDate = new Date().toISOString().split('T')[0];

    exportDataGrid({
      component: this.dxAdminPortfolioPropertiesDataGrid.instance,
      worksheet,
      autoFilterEnabled: true,
      customizeCell: ({ gridCell, excelCell }) => {
        if (gridCell.rowType === 'data') {
          if (gridCell.column.dataField === 'isActive') {
            excelCell.value = gridCell.data.isActive === true ? 'Active' : 'Inactive';
          }
        }
      },
    }).then(() => {
      workbook.xlsx.writeBuffer().then((buffer) => {
        saveAs(new Blob([buffer], { type: 'application/octet-stream' }), `${this.companyName?.trim() ?? this.workspaceName?.trim()}-Portfolio-Properties-${currentDate}.xlsx`);
      });
      
      this.analyticsService.reportExportedEvent(AnalyticsFileFormat.Excel, AnalyticsReportType.PortfolioProperties);
    });
  }

  deactivateProperty() {
    this.deactivate.emit(this.toggleStatusPropertyId);
    this.toggleStatusPropertyId = null;
  }

  reactivateProperty() {
    this.reactivate.emit(this.toggleStatusPropertyId);
    this.toggleStatusPropertyId = null;
  }

  onReassignPropertyConfirm(data: any) {
    this.reassign.emit({ propertyId: data.propertyId, destinationWorkspaceId: data.destinationWorkspaceId, includeUsers: data.includeUsers });
  }

  duplicateProperty() {
    this.duplicate.emit(this.duplicatePropertyId);
    this.duplicatePropertyId = null;
  }
}
